import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Permission } from '@/types';
import { Shield, Lock } from 'lucide-react';
import { usePermissions } from '@/store/permission';
import { useResources } from '@/store/resource';
import { Button } from '@/components/ui/Button';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { ComposedTable, StatusCell, BadgeCell, ActionCell, type FilterDef, type FilterValues } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

export const PermissionsPage = () => {
  const [perms, setPerms] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const listPermissions = usePermissions((state) => state.list);
  const { resourceKinds, listResourceKinds } = useResources();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  useEffect(() => {
    setBreadcrumbs([{ name: 'Permissions' }]);
    Promise.all([
      listResourceKinds(),
      listPermissions().then(setPerms)
    ]).catch(console.error).finally(() => setLoading(false));
  }, [listPermissions, listResourceKinds, setBreadcrumbs]);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { type: 'chips', key: 'resource_kinds', label: 'Resource Kind', options: resourceKinds.map(k => k.id) },
  ], [resourceKinds]);

  const columns = [
    {
      id: 'name',
      header: 'Permission Key',
      accessorKey: 'name',
      cell: ({ row }: any) => {
        const p = row.original as Permission;
        return (
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center border border-slate-200/60 dark:border-slate-700/60 shadow bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover:bg-orange-50 group-hover:text-orange-600 dark:group-hover:bg-orange-500/10 dark:group-hover:text-orange-500 shrink-0 transition-colors duration-300">
              <Lock className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-[14px] font-bold font-mono tracking-tight text-slate-900 dark:text-white cursor-pointer hover:text-[--primary]" onClick={() => navigate(`/m/permissions/${p.id}`)}>
                {p.name}
              </span>
              <span className="text-xs text-slate-500 mt-0.5 max-w-[200px] truncate">{p.description || 'Access Scope'}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: () => <StatusCell status={"Active"} />,
    },
    {
      id: 'resource_kind',
      header: 'Resource Kind',
      cell: ({ row }: any) => {
        const p = row.original as Permission;
        return (
          <div className="flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <BadgeCell label={p.resource_kind_id || 'None'} />
          </div>
        );
      },
    },
    {
      id: 'created',
      header: 'Created Date',
      cell: ({ row }: any) => <span className="text-[13px] text-slate-500 dark:text-slate-400">{row.original.created_at ? new Date(row.original.created_at).toLocaleDateString() : ""}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: any) => <ActionCell onClick={() => navigate(`/m/permissions/${row.original.id}`)} />,
    },
  ];

  const filterFn = (row: Permission, values: FilterValues) => {
    const kinds = values['resource_kinds'] as string[];
    if (kinds?.length > 0 && !kinds.includes(row.resource_kind_id)) return false;
    return true;
  };

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No permissions found</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Start by creating your first system permission.</p>
      <Button className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6" onClick={() => navigate('/m/permissions/create')}>Create first permission</Button>
    </div>
  );

  return (
    <PageLayout title="Permissions" description="Manage platform and resource level permission definitions.">
      {loading ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900"><Loader /></div>
      ) : (
        <ComposedTable
          data={perms}
          columns={columns}
          searchPlaceholder="Search permissions by key or description..."
          statusOptions={['All', 'Active', 'Inactive']}
          statusField={() => "Active"}
          filters={filterDefs}
          filterFn={filterFn}
          onAdd={() => navigate('/m/permissions/create')}
          emptyState={emptyState}
          itemLabel="permissions"
        />
      )}
    </PageLayout>
  );
};
